(() => {
class LvProductHeroController {
  constructor(root) {
    this.root = root;
    this.panels = [...root.querySelectorAll('[data-lv-media-panel]')];
    this.thumbs = [...root.querySelectorAll('[data-lv-media-thumb]')];
    this.variants = this.readJson('[data-lv-product-variants]', []);
    this.config = this.readJson('[data-lv-product-config]', {});
    this.fsaDialog = root.querySelector('[data-lv-fsa-dialog]');
    this.fsaOpenButton = root.querySelector('[data-lv-fsa-open]');
    this.fsaReturnFocus = null;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMedia(thumb.dataset.lvMediaThumb)));
    root.querySelector('[data-lv-gallery-prev]')?.addEventListener('click', () => this.stepGallery(-1));
    root.querySelector('[data-lv-gallery-next]')?.addEventListener('click', () => this.stepGallery(1));
    root.querySelectorAll('[data-lv-option]').forEach((select) => select.addEventListener('change', () => this.updateVariant()));
    this.fsaOpenButton?.addEventListener('click', () => this.openFsaDialog());
    this.fsaDialog?.querySelectorAll('[data-lv-fsa-close]').forEach((button) => button.addEventListener('click', () => this.closeFsaDialog()));
    this.fsaDialog?.addEventListener('click', (event) => {
      if (event.target === this.fsaDialog) this.closeFsaDialog();
    });
    this.fsaDialog?.addEventListener('close', () => this.fsaReturnFocus?.focus());
  }

  readJson(selector, fallback) {
    try {
      return JSON.parse(this.root.querySelector(selector)?.textContent || '');
    } catch (_error) {
      return fallback;
    }
  }

  showMedia(mediaId) {
    if (!mediaId) return;
    this.panels.forEach((panel) => {
      const active = panel.dataset.lvMediaPanel === String(mediaId);
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (!active) panel.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.lvMediaThumb === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
      if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }

  stepGallery(direction) {
    if (!this.thumbs.length) return;
    const current = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
    const next = (current + direction + this.thumbs.length) % this.thumbs.length;
    this.showMedia(this.thumbs[next].dataset.lvMediaThumb);
  }

  updateVariant() {
    const selected = [...this.root.querySelectorAll('[data-lv-option]')].map((select) => select.value);
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
    if (!variant) return;

    const productInput = this.root.querySelector('[data-lv-product-variant]');
    const installmentInput = this.root.querySelector('[data-lv-installment-variant]');
    if (productInput) {
      productInput.value = variant.id;
      productInput.disabled = !variant.available;
      productInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (installmentInput) {
      installmentInput.value = variant.id;
      installmentInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updatePrice(variant);
    this.updateAvailability(variant);
    if (variant.featured_media?.id) this.showMedia(variant.featured_media.id);

    if (!window.Shopify?.designMode) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  updatePrice(variant) {
    const price = this.formatMoney(variant.price);
    const compare = variant.compare_at_price && variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : '';
    const savings = compare ? this.formatMoney(variant.compare_at_price - variant.price) : '';
    const priceNode = this.root.querySelector('[data-lv-price]');
    const compareNode = this.root.querySelector('[data-lv-compare]');
    const savingsNode = this.root.querySelector('[data-lv-savings]');
    if (priceNode) priceNode.textContent = price;
    if (compareNode) {
      compareNode.textContent = compare;
      compareNode.classList.toggle('is-hidden', !compare);
    }
    if (savingsNode) {
      savingsNode.textContent = savings ? `${this.config.savingsLabel || 'Save'} ${savings}` : '';
      savingsNode.classList.toggle('is-hidden', !savings);
    }
    const buyLabel = this.root.querySelector('[data-lv-buy-label]');
    if (buyLabel && variant.available) buyLabel.textContent = `${this.config.buyLabel || 'Add to cart'} · ${price}`;
  }

  updateAvailability(variant) {
    const button = this.root.querySelector('.lv-product-hero__buy');
    const label = this.root.querySelector('[data-lv-buy-label]');
    const stock = this.root.querySelector('[data-lv-stock]');
    button?.toggleAttribute('disabled', !variant.available);
    if (label && !variant.available) label.textContent = this.config.soldOutLabel || 'Sold out';
    if (stock) {
      stock.classList.toggle('is-sold-out', !variant.available);
      const stockText = stock.querySelector('span');
      if (stockText) stockText.textContent = variant.available ? this.config.inStockLabel || 'In Stock' : this.config.soldOutLabel || 'Sold out';
    }
  }

  openFsaDialog() {
    if (!this.fsaDialog) return;
    this.fsaReturnFocus = document.activeElement;
    if (typeof this.fsaDialog.showModal === 'function') {
      if (!this.fsaDialog.open) this.fsaDialog.showModal();
    } else {
      this.fsaDialog.setAttribute('open', '');
    }
  }

  closeFsaDialog() {
    if (!this.fsaDialog) return;
    if (typeof this.fsaDialog.close === 'function' && this.fsaDialog.open) {
      this.fsaDialog.close();
    } else {
      this.fsaDialog.removeAttribute('open');
      this.fsaReturnFocus?.focus();
    }
  }

  formatMoney(cents) {
    const format = this.config.moneyFormat || '${{amount}}';
    const value = (Number(cents || 0) / 100).toFixed(2);
    const noDecimals = Math.round(Number(cents || 0) / 100).toString();
    return format
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount}}', value)
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals);
  }
}

const initLvProductHeroes = (scope = document) => {
  scope.querySelectorAll('[data-lv-product-hero]:not([data-lv-initialized])').forEach((root) => {
    root.dataset.lvInitialized = 'true';
    new LvProductHeroController(root);
  });
};

initLvProductHeroes();
document.addEventListener('shopify:section:load', (event) => initLvProductHeroes(event.target));
})();
